import { Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

// Importaciones de entornos.
import { environment } from '../../../environments/environment';

// Importaciones de servicios.
import { LicenseService } from '../../services/license.service';
import { LogService } from '../../services/log.service';

// Importaciones de interfaces.
import { ILicense } from '../../interfaces/license.interface';

// Importaciones de modelos.
import { Log } from '../../models/log.model';

/**
 * Componente de diálogo para la eliminación de la licencia.
 *
 * Este componente proporciona una interfaz para confirmar y realizar la eliminación de la licencia.
 * Se muestra como un cuadro de diálogo, permitiendo al usuario confirmar la acción antes de proceder con la eliminación.
 */
@Component({
  selector: 'app-license-delete-dialog',
  templateUrl: './license-delete-dialog.component.html',
  standalone: false,
})
export class LicenseDeleteDialogComponent {
  /**
   * Referencia al diálogo actual.
   *
   * Se utiliza para controlar y cerrar el cuadro de diálogo de eliminación de la licencia.
   */
  readonly dialogRef: MatDialogRef<LicenseDeleteDialogComponent> = inject(
    MatDialogRef<LicenseDeleteDialogComponent>
  );

  /**
   * Datos de la licencia proporcionados al diálogo.
   *
   * Se inyectan desde MAT_DIALOG_DATA y son de solo lectura.
   */
  private readonly license: ILicense = inject<ILicense>(MAT_DIALOG_DATA);

  /**
   * Indica si la operación de eliminado está en curso.
   *
   * Se utiliza para deshabilitar la interfaz y mostrar un indicador de carga mientras se elimina la licencia.
   */
  loading: boolean = false;

  /**
   * Lista de errores.
   *
   * Contiene los mensajes de error generados si la operación de eliminado falla.
   */
  errors: string[] = [];

  /**
   * Constructor del componente.
   *
   * Este constructor inyecta las dependencias necesarias para el funcionamiento del componente.
   *
   * @param licenseService - Servicio para gestionar la información de las licencias.
   * @param logService - Servicio para gestionar los registros.
   * @param snackBar - Servicio para mostrar notificaciones de tipo snack bar.
   */
  constructor(
    private licenseService: LicenseService,
    private logService: LogService,
    private snackBar: MatSnackBar
  ) {}

  /**
   * Confirma y elimina la licencia actual.
   *
   * Muestra un diálogo de confirmación antes de proceder con la eliminación.
   * Si la eliminación es exitosa, cierra el diálogo.
   */
  async delete(): Promise<void> {
    this.loading = true;
    this.errors = [];

    try {
      const licenseResponse: string = await this.licenseService.delete(
        this.license
      );

      // Añadir un registro de eliminación de la licencia.
      try {
        await this.logService.create(
          new Log({
            description: `Ha eliminado la licencia '${this.license.id}'.`,
          })
        );
      } catch (errors: any) {
        if (!environment.production) {
          console.warn(
            'No se ha podido guardar el registro de eliminación de la licencia.'
          );
        }
      }

      this.snackBar.open(licenseResponse, '', { duration: 5000 });

      this.dialogRef.close(true);
    } catch (errors: any) {
      this.errors = errors;
    }

    this.loading = false;
  }
}
